import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, SearchX, MapPin } from "lucide-react";
import { ActivityCard } from "@/components/ActivityCard";
import { AUDIENCES, SUBCATEGORIES, SPORTS, CITIES } from "@/constants/categories";
import { preloadImages } from "@/lib/preloadImages";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { wallpaperStyle, WALLPAPER_TOP } from "@/lib/wallpaper";

const SubcategoryPage = () => {
  const { audience: audParam, subcat, group } = useParams();
  const audience = group ? (group === "enilikes" ? "enilikes" : "paidia") : audParam;
  const ageLabel = group && (group === "enilikes" ? "Ενήλικες" : `${group.replace("-", " – ")} ετών`);
  const { t: tr, tt } = useLang();
  const [searchParams] = useSearchParams();
  const sportSlug = searchParams.get("sport") || "";
  const navigate = useNavigate();
  const a = AUDIENCES[audience];
  const sub = SUBCATEGORIES[subcat];
  const sport = sportSlug ? SPORTS[sportSlug] : null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [city, setCity] = useState("");
  const PAGE = 9;

  const baseParams = () => {
    const params = group
      ? (group === "enilikes" ? { audience: "enilikes" } : { age_group: group })
      : { subcategory: subcat, audience };
    Object.assign(params, { sort: "rating", limit: PAGE });
    if (sportSlug) params.sport = sportSlug;
    if (city) params.city = city;
    return params;
  };

  useEffect(() => {
    setLoading(true);
    api.get("/businesses", { params: { ...baseParams(), skip: 0 } })
      .then((res) => preloadImages(res.data).then(() => { setItems(res.data); setHasMore(res.data.length === PAGE); }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcat, audience, sportSlug, city, group]);

  const loadMore = () => {
    setLoadingMore(true);
    api.get("/businesses", { params: { ...baseParams(), skip: items.length } })
      .then((res) => preloadImages(res.data).then(() => { setItems((prev) => [...prev, ...res.data]); setHasMore(res.data.length === PAGE); }))
      .finally(() => setLoadingMore(false));
  };

  if (!a || (!sub && !group)) {
    navigate("/");
    return null;
  }

  const heading = group ? ageLabel : sport ? tt(sport) : tt(sub);

  return (
    <div className="relative min-h-screen" style={wallpaperStyle(audience)}>
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center text-slate-900">
          <button
            onClick={() => (group ? navigate(-1) : navigate(sportSlug ? `/sports/${audience}` : `/c/${audience}`))}
            data-testid="subcat-back-button"
            className="h-10 w-10 grid place-items-center rounded-full bg-white/60 hover:bg-white/90 transition-colors shrink-0"
          >
            <ArrowLeft size={22} />
          </button>
        </div>
      </header>

      <main className="relative max-w-5xl mx-auto px-4 pb-6" style={WALLPAPER_TOP}>
        <h1 className="mb-4 text-center font-display text-xl sm:text-2xl font-extrabold leading-tight text-[#0B2454]" data-testid="subcat-title">{heading}</h1>
        {/* City filter */}
        <div className="flex items-center gap-2 mb-6">
          <MapPin size={18} className="text-slate-700" />
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            data-testid="subcat-city-select"
            className="h-10 px-3 rounded-xl bg-white/90 text-slate-900 font-semibold text-sm outline-none cursor-pointer shadow-sm"
          >
            <option value="">{tr("subcat.allCities")}</option>
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-72 rounded-2xl bg-white/50 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-slate-700" data-testid="subcat-empty">
            <SearchX className="mx-auto mb-3" size={44} />
            <p className="font-semibold text-lg">{tr("subcat.empty")}{city ? ` — ${city}` : ""}</p>
            <p className="text-slate-500 text-sm mt-1">{tr("subcat.emptyHint")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="subcat-business-grid">
            {items.map((b, i) => <ActivityCard audience={audience} key={b.id} business={b} index={i % PAGE} />)}
          </div>
        )}
        {!loading && hasMore && (
          <div className="flex justify-center mt-8">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="subcat-load-more"
              className="px-6 py-3 rounded-full bg-white/90 text-slate-900 font-display font-bold shadow-sm hover:shadow-md hover:bg-white transition-all disabled:opacity-60"
            >
              {loadingMore ? "…" : (tr("subcat.loadMore") || "Φόρτωση περισσότερων")}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SubcategoryPage;
